import { useQuery } from '@apollo/client';
import { useEffect } from 'react';

import {
  FileUploadFragment,
  GET_GITHUB_EMAIL_MAPPING_DATA,
  GetGithubEmailMappingDataQuery,
  GetGithubEmailMappingDataQueryVariables,
  TicketingProvider,
} from '@neo.tax/shared/graphql';

type UseGithubEmailMappingDataParams = {
  companyId: string;
  syncedTicketingProviders: TicketingProvider[];
};

type UseGithubEmailMappingDataReturn = {
  shouldPromptUpload: boolean;
  shouldShowUploadButton: boolean;
  uploads: FileUploadFragment[];
};

const VALIDATING_POLL_INTERVAL_MS = 3_000;

/**
 * A company on other providers skips the request entirely rather than paying for a result the page
 * would discard.
 *
 * While an upload is awaiting a validation result this polls, so the upload's row settles on its own
 * — whether the upload was started here or was already in flight when the page loaded. The shared
 * background-operation poll cannot serve this: it runs once a minute and only reacts to an operation
 * it saw incomplete, so a flow finishing in seconds is never observed.
 */
export const useGithubEmailMappingData = ({
  companyId,
  syncedTicketingProviders,
}: UseGithubEmailMappingDataParams): UseGithubEmailMappingDataReturn => {
  const hasGithubData = syncedTicketingProviders.includes(TicketingProvider.GithubIssues);

  const { data, loading, startPolling, stopPolling } = useQuery<
    GetGithubEmailMappingDataQuery,
    GetGithubEmailMappingDataQueryVariables
  >(GET_GITHUB_EMAIL_MAPPING_DATA, {
    variables: { companyId },
    skip: !hasGithubData,
  });

  const uploads = data?.getCompany?.githubEmailMappingUploads ?? [];
  const hasValidatingUpload = uploads.some((upload) => upload.isValid === null);

  useEffect(() => {
    if (hasValidatingUpload) {
      startPolling(VALIDATING_POLL_INTERVAL_MS);
      return;
    }
    stopPolling();
  }, [hasValidatingUpload, startPolling, stopPolling]);

  // A failed upload leaves the mapping unapplied, so the prompt stays until one lands that has not
  // failed. Once one has, the upload moves to the Data Sources button so the mapping can still be
  // added to. Both stay hidden until the uploads have loaded, since until then every company looks
  // like one that has never uploaded.
  const hasLoadedUploads = hasGithubData && !loading;
  const hasNonFailedUpload = uploads.some((upload) => upload.isValid !== false);

  return {
    shouldPromptUpload: hasLoadedUploads && !hasNonFailedUpload,
    shouldShowUploadButton: hasLoadedUploads && hasNonFailedUpload,
    uploads,
  };
};
